import type {
  Action,
  ActionsListResponse,
  CreateActionInput,
  PatchActionRequest,
  RunActionRequest,
  RunStarted,
} from '@logspot-platform/platform-types';
import { api } from './api-client';

type SuccessEnvelope<T> = { data: T };

export type { Action } from '@logspot-platform/platform-types';

// Org-scoped actions — user-saved instances of a single catalog
// action (runnable once or on a schedule). Mirrors the backend admin endpoints
// (api/admin/actions.ts).
export const ActionsService = {
  list: async (): Promise<Action[]> => {
    const r =
      await api.get<SuccessEnvelope<ActionsListResponse>>('/admin/actions');
    return r.data.actions;
  },

  get: async (id: string): Promise<Action> => {
    const r = await api.get<SuccessEnvelope<{ action_instance: Action }>>(
      `/admin/actions/${id}`,
    );
    return r.data.action_instance;
  },

  create: async (input: CreateActionInput): Promise<Action> => {
    const r = await api.post<SuccessEnvelope<{ action_instance: Action }>>(
      '/admin/actions',
      input,
    );
    return r.data.action_instance;
  },

  update: async (id: string, input: PatchActionRequest): Promise<Action> => {
    const r = await api.put<SuccessEnvelope<{ action_instance: Action }>>(
      `/admin/actions/${id}`,
      input,
    );
    return r.data.action_instance;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/admin/actions/${id}`);
  },

  sendTest: async (
    id: string,
  ): Promise<{ delivered: number; failures: string[] }> => {
    const r = await api.post<
      SuccessEnvelope<{ delivered: number; failures: string[] }>
    >(`/admin/actions/${id}/test`, {});
    return r.data;
  },

  // The server adds `run_id` and `status` only while dispatch is on.
  run: async (
    id: string,
    input: RunActionRequest = {},
  ): Promise<{ ok: true } & Partial<RunStarted>> => {
    const r = await api.post<
      SuccessEnvelope<{ ok: true } & Partial<RunStarted>>
    >(`/admin/actions/${id}/run`, input);
    return r.data;
  },
};
